/**
 * BridgeCard Component
 * Represents one Objective artifact_item with pillar-based layout:
 * - Header: Artifact identity + Entropy context (Current → Future)
 * - Left Pillar: Current State (top) + Assumptions (bottom)
 * - Gap Area: Horizontal planks bridging the gap + Items to Change (bottom)
 * - Right Pillar: Future State with target entropy (top) + Necessary Conditions (bottom)
 * Completion logic: Bridge is complete when every condition is supported by ≥1 plank
 * and at least one structural plank exists.
 * @feature BRIDGE_CARD - Objective + improvement artifact_items; view/edit Bridge Card
 */

import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Typography, Input, Button, Space, Tag } from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ArrowRightOutlined,
} from '@ant-design/icons';
import { BridgeCardProps, Improvement, AssumptionChange, ActionImprovement, BridgeCardObjective, ChangeType, ChangeItem } from '../../types/bridgeCard';
import { BridgeCardFinancialChart } from './BridgeCardFinancialChart';
import styles from './BridgeCard.module.css';

const { Title, Text } = Typography;
const { TextArea } = Input;

/** US currency, no decimals (for valuation, revenue, expense, toll). */
function formatCurrency(n: number): string {
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0, minimumFractionDigits: 0 });
}

/** Value ratio: 2 decimal places. */
function formatValueRatio(n: number): string {
  return n.toFixed(2);
}

/** % improvement from current to future value ratio. Returns null if not computable. */
function formatValueRatioImprovement(current: number | undefined, future: number | undefined): { text: string; positive: boolean } | null {
  if (current == null || future == null || current <= 0) return null;
  const pct = ((future - current) / current) * 100;
  const sign = pct >= 0 ? '+' : '';
  return { text: `${sign}${pct.toFixed(1)}%`, positive: pct >= 0 };
}

/** Plain number for value/day, value/hour (could be large). */
function formatNumber(n: number): string {
  return n.toLocaleString();
}

export const BridgeCard: React.FC<BridgeCardProps> = ({ objective, onUpdate }) => {
  const navigate = useNavigate();
  const [editingPlankId, setEditingPlankId] = useState<string | null>(null);
  const [editingConditionIndex, setEditingConditionIndex] = useState<number | null>(null);
  const [editingAssumptionIndex, setEditingAssumptionIndex] = useState<number | null>(null);

  // Separate improvements into structural (assumption changes) and light (actions)
  const structuralPlanks = useMemo(() => {
    return objective.plan.improvements.filter(
      (imp): imp is AssumptionChange => imp.type === 'assumption_change'
    );
  }, [objective.plan.improvements]);

  const lightPlanks = useMemo(() => {
    return objective.plan.improvements.filter(
      (imp): imp is ActionImprovement => imp.type === 'action'
    );
  }, [objective.plan.improvements]);

  // Check if bridge is complete
  const isComplete = useMemo(() => {
    if (structuralPlanks.length === 0) return false;
    
    // Check if every condition is supported by at least one plank
    const supportedConditions = new Set<number>();
    objective.plan.improvements.forEach(imp => {
      if (imp.type === 'assumption_change') {
        const assumptionChange = imp as AssumptionChange;
        if (assumptionChange.addresses_condition_index !== undefined) {
          supportedConditions.add(assumptionChange.addresses_condition_index);
        }
      }
      // Also check if plank has change_type (indicates it's complete)
      if (imp.change_type) {
        // If it's an assumption change without explicit condition link, assume it supports all conditions
        if (imp.type === 'assumption_change') {
          const assumptionChange = imp as AssumptionChange;
          if (assumptionChange.addresses_condition_index === undefined) {
            objective.necessary_conditions.forEach((_, index) => {
              supportedConditions.add(index);
            });
          }
        }
      }
    });

    const conditionCount = objective.necessary_conditions.length;
    return conditionCount > 0 && supportedConditions.size === conditionCount;
  }, [structuralPlanks, objective.plan.improvements, objective.necessary_conditions]);

  // Get entropy color based on score
  const getEntropyColor = (score: number): string => {
    if (score >= 8) return '#ff4d4f'; // Red
    if (score >= 6) return '#faad14'; // Orange
    if (score >= 4) return '#fa8c16'; // Light orange
    return '#52c41a'; // Green
  };

  const handleAddStructuralPlank = (assumptionIndex?: number, changeType?: ChangeType) => {
    const newPlank: AssumptionChange = {
      id: `imp-${Date.now()}`,
      type: 'assumption_change',
      belief_old: '',
      belief_new: '',
      change_type: changeType,
      addresses_assumption_index: assumptionIndex,
    };
    const updatedObjective = {
      ...objective,
      plan: {
        improvements: [...objective.plan.improvements, newPlank],
      },
      status: 'incomplete' as const,
    };
    onUpdate?.(updatedObjective);
    setEditingPlankId(newPlank.id);
  };

  const handleAddLightPlank = (parentId: string) => {
    const newPlank: ActionImprovement = {
      id: `imp-${Date.now()}`,
      type: 'action',
      description: '',
      parent_improvement_id: parentId,
    };
    const updatedObjective = {
      ...objective,
      plan: {
        improvements: [...objective.plan.improvements, newPlank],
      },
    };
    onUpdate?.(updatedObjective);
    setEditingPlankId(newPlank.id);
  };

  const handleUpdatePlank = (plankId: string, updates: Partial<Improvement>) => {
    const updatedImprovements: Improvement[] = objective.plan.improvements.map(imp =>
      imp.id === plankId ? { ...imp, ...updates } as Improvement : imp
    );
    const updatedObjective: BridgeCardObjective = {
      ...objective,
      plan: {
        improvements: updatedImprovements,
      },
    };
    onUpdate?.(updatedObjective);
  };

  const handleDeletePlank = (plankId: string) => {
    const updatedImprovements: Improvement[] = objective.plan.improvements.filter(imp => imp.id !== plankId);
    const updatedObjective: BridgeCardObjective = {
      ...objective,
      plan: {
        improvements: updatedImprovements,
      },
      status: updatedImprovements.filter(imp => imp.type === 'assumption_change').length === 0
        ? 'incomplete' as const
        : objective.status,
    };
    onUpdate?.(updatedObjective);
  };

  const handleAddCondition = () => {
    const updatedObjective: BridgeCardObjective = {
      ...objective,
      necessary_conditions: [...objective.necessary_conditions, { condition: '' }],
    };
    onUpdate?.(updatedObjective);
    setEditingConditionIndex(updatedObjective.necessary_conditions.length - 1);
  };

  const handleUpdateCondition = (index: number, value: string) => {
    const updatedConditions = [...objective.necessary_conditions];
    const existing = updatedConditions[index];
    updatedConditions[index] = { ...existing, condition: value };
    const updatedObjective: BridgeCardObjective = {
      ...objective,
      necessary_conditions: updatedConditions,
    };
    onUpdate?.(updatedObjective);
  };

  const handleDeleteCondition = (index: number) => {
    const updatedConditions = objective.necessary_conditions.filter((_, i) => i !== index);
    const updatedObjective: BridgeCardObjective = {
      ...objective,
      necessary_conditions: updatedConditions,
    };
    onUpdate?.(updatedObjective);
  };

  // Assumption handlers
  const handleAddAssumption = () => {
    const updatedObjective: BridgeCardObjective = {
      ...objective,
      assumptions: [...objective.assumptions, ''],
    };
    onUpdate?.(updatedObjective);
    setEditingAssumptionIndex(updatedObjective.assumptions.length - 1);
  };

  const handleUpdateAssumption = (index: number, value: string) => {
    const updatedAssumptions = [...objective.assumptions];
    updatedAssumptions[index] = value;
    const updatedObjective: BridgeCardObjective = {
      ...objective,
      assumptions: updatedAssumptions,
    };
    onUpdate?.(updatedObjective);
  };

  const handleDeleteAssumption = (index: number) => {
    const updatedAssumptions = objective.assumptions.filter((_, i) => i !== index);
    const updatedObjective: BridgeCardObjective = {
      ...objective,
      assumptions: updatedAssumptions,
    };
    onUpdate?.(updatedObjective);
  };

  // Future state handlers
  const handleSetFutureState = (targetEntropy: number) => {
    const updatedObjective: BridgeCardObjective = {
      ...objective,
      future_state: {
        entropy_score_target: targetEntropy,
        entropy_score_current: objective.entropy_score_current || objective.entropy_score_start,
      },
    };
    onUpdate?.(updatedObjective);
  };

  // Generate items to change from planks
  const generateItemsToChange = useMemo((): ChangeItem[] => {
    const items: ChangeItem[] = [];
    objective.plan.improvements.forEach(plank => {
      if (plank.change_type) {
        if (plank.type === 'assumption_change') {
          items.push({
            id: `change-${plank.id}`,
            description: `Change belief: "${plank.belief_old}" → "${plank.belief_new}"`,
            change_type: plank.change_type,
            plank_id: plank.id,
          });
        } else if (plank.type === 'action') {
          items.push({
            id: `change-${plank.id}`,
            description: plank.description,
            change_type: plank.change_type,
            plank_id: plank.id,
          });
        }
      }
    });
    return items;
  }, [objective.plan.improvements]);

  // Update items to change when planks change
  useEffect(() => {
    const items = generateItemsToChange;
    const currentItems = objective.items_to_change || [];
    if (JSON.stringify(items) !== JSON.stringify(currentItems)) {
      const updatedObjective: BridgeCardObjective = {
        ...objective,
        items_to_change: items,
      };
      onUpdate?.(updatedObjective);
    }
  }, [generateItemsToChange, objective.items_to_change, objective, onUpdate]);

  // Update plank change type
  const handleUpdatePlankChangeType = (plankId: string, changeType: ChangeType) => {
    handleUpdatePlank(plankId, { change_type: changeType });
  };

  const getLightPlanksForStructural = (structuralId: string) => {
    return lightPlanks.filter(plank => plank.parent_improvement_id === structuralId);
  };

  const planks = objective.friction_planks ?? [];
  const valueRatioImprovement = formatValueRatioImprovement(
    objective.value_ratio_current,
    objective.value_ratio_future,
  );

  return (
    <div className={styles.bridgeCard}>
      {/* Financial chart: Actual vs Ideal + Future projection (correction factors) */}
      <div className={styles.financialChartSection}>
        <BridgeCardFinancialChart objective={objective} />
      </div>

      {/* Bridge visual: Current (left) → Bridge (center) → Future (right) */}
      <section className={styles.bridgeObjectiveSection}>
        <div className={styles.bridgeStrip}>
          <div className={styles.bridgeZoneCurrent}>
            <div className={styles.bridgeZoneLabel}>Current state</div>
            <div className={styles.compactFields}>
              <div className={`${styles.compactRow} ${styles.valueRatioRow}`}>
                <span className={styles.compactLabel}>Value ratio</span>
                <span className={styles.valueRatioValue}>{objective.value_ratio_current != null ? formatValueRatio(objective.value_ratio_current) : '—'}</span>
              </div>
              <div className={styles.compactRow}><span className={styles.compactLabel}>Current valuation</span><span>{objective.current_valuation != null ? formatCurrency(objective.current_valuation) : '—'}</span></div>
              <div className={styles.compactRow}><span className={styles.compactLabel}>Revenue</span><span>{objective.current_revenue != null ? formatCurrency(objective.current_revenue) : '—'}</span></div>
              <div className={styles.compactRow}><span className={styles.compactLabel}>Expenses</span><span>{objective.current_expense != null ? formatCurrency(objective.current_expense) : '—'}</span></div>
            </div>
          </div>
          <div className={styles.bridgeConnector} aria-hidden>
            <span className={styles.bridgeConnectorArrow}>→</span>
            <span className={styles.bridgeConnectorText}>Bridge</span>
          </div>
          <div className={styles.bridgeZoneBridge}>
            <div className={styles.bridgeZoneLabel}>Transition</div>
            <div className={styles.compactFields}>
              {valueRatioImprovement != null && (
                <div className={`${styles.compactRow} ${styles.valueRatioRow}`}>
                  <span className={styles.compactLabel}>Value ratio improvement</span>
                  <span className={valueRatioImprovement.positive ? styles.valueRatioImprovement : styles.valueRatioDecline}>
                    {valueRatioImprovement.text}
                  </span>
                </div>
              )}
              <div className={styles.compactRow}><span className={styles.compactLabel}>Time to transition</span><span>{objective.estimated_time_to_transition ?? '—'}</span></div>
              <div className={styles.compactRow}><span className={styles.compactLabel}>Value/day</span><span>{objective.value_per_day != null ? formatCurrency(objective.value_per_day) : '—'}</span></div>
              <div className={styles.compactRow}><span className={styles.compactLabel}>Value/hour</span><span>{objective.value_per_hour != null ? formatCurrency(objective.value_per_hour) : '—'}</span></div>
              <div className={styles.compactRow}><span className={styles.compactLabel}>Toll (time)</span><span>{objective.commitment_toll_time ?? '—'}</span></div>
              <div className={styles.compactRow}><span className={styles.compactLabel}>Toll (charge)</span><span>{objective.commitment_toll_cost_1hr != null && objective.commitment_toll_cost_1hr > 0 ? formatCurrency(objective.commitment_toll_cost_1hr) : '—'}</span></div>
            </div>
          </div>
          <div className={styles.bridgeConnector} aria-hidden>
            <span className={styles.bridgeConnectorArrow}>→</span>
            <span className={styles.bridgeConnectorText}>Future</span>
          </div>
          <div className={styles.bridgeZoneFuture}>
            <div className={styles.bridgeZoneLabel}>Future state</div>
            <div className={styles.compactFields}>
              <div className={`${styles.compactRow} ${styles.valueRatioRow}`}>
                <span className={styles.compactLabel}>Value ratio</span>
                <span className={styles.valueRatioValue}>{objective.value_ratio_future != null ? formatValueRatio(objective.value_ratio_future) : '—'}</span>
              </div>
              <div className={styles.compactRow}><span className={styles.compactLabel}>Objective</span><span className={styles.compactValue}>{objective.bridge_objective ?? objective.improvement_objective ?? '—'}</span></div>
              <div className={styles.compactRow}><span className={styles.compactLabel}>Future valuation</span><span>{objective.target_valuation != null ? formatCurrency(objective.target_valuation) : '—'}</span></div>
              <div className={styles.compactRow}><span className={styles.compactLabel}>Revenue</span><span>{objective.future_revenue != null ? formatCurrency(objective.future_revenue) : '—'}</span></div>
              <div className={styles.compactRow}><span className={styles.compactLabel}>Expense</span><span>{objective.future_expense != null ? formatCurrency(objective.future_expense) : '—'}</span></div>
            </div>
          </div>
        </div>
      </section>

      {/* Friction Planks: same left (current) | center (bridge) | right (future) */}
      <section className={styles.frictionPlanksSection}>
        <div className={styles.sectionMainTitle}>Friction planks</div>
        {planks.length === 0 ? (
          <Text type="secondary" italic className={styles.emptyPlanks}>No friction planks yet. Complete Friction Priorities and Define Conditions.</Text>
        ) : (
          <div className={styles.frictionPlanksList}>
            {planks.map((plank, index) => (
              <div key={index} className={styles.frictionPlankItem}>
                <div className={styles.plankQuestion}>{plank.question_text}</div>
                <div className={styles.plankStrip}>
                  <div className={styles.plankZoneCurrent}>
                    <div className={styles.plankZoneLabel}>Assumption</div>
                    <div className={styles.plankCellContent}>{plank.assumption || '—'}</div>
                    <div className={styles.compactRow}><span className={styles.compactLabel}>Score</span><span>{plank.current_entropy_score != null ? plank.current_entropy_score : '—'}</span></div>
                  </div>
                  <div className={styles.plankConnector} aria-hidden>→</div>
                  <div className={styles.plankZoneBridge}>
                    <div className={styles.plankZoneLabel}>Action</div>
                    <div className={styles.plankCellContent}>{plank.action || '—'}</div>
                    <div className={styles.compactRow}><span className={styles.compactLabel}>Assignee</span><span>{plank.assignee || '—'}</span></div>
                    <div className={styles.compactRow}><span className={styles.compactLabel}>Duration</span><span>{plank.duration || '—'}</span></div>
                  </div>
                  <div className={styles.plankConnector} aria-hidden>→</div>
                  <div className={styles.plankZoneFuture}>
                    <div className={styles.plankZoneLabel}>Future condition</div>
                    <div className={styles.plankCellContent}>{plank.necessary_condition || '—'}</div>
                    <div className={styles.compactRow}><span className={styles.compactLabel}>Score</span><span>{plank.future_entropy_score != null ? plank.future_entropy_score : '—'}</span></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Completion Status */}
      <Card className={styles.statusCard} bordered={false}>
        <div className={styles.statusContent}>
          {isComplete ? (
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
              <Space>
                <CheckCircleOutlined className={styles.completeIcon} />
                <Text strong>Bridge Complete</Text>
                <Text type="secondary">All conditions are supported by planks.</Text>
              </Space>
              <Button
                type="primary"
                size="large"
                icon={<ArrowRightOutlined />}
                onClick={() => navigate(`/dashboard?objectiveId=${objective.id}`)}
                className={styles.controlBridgeButton}
              >
                Open Control Bridge
              </Button>
            </Space>
          ) : (
            <Space>
              <CloseCircleOutlined className={styles.incompleteIcon} />
              <Text>Bridge Incomplete</Text>
              <Text type="secondary">
                {structuralPlanks.length === 0
                  ? 'Add at least one assumption change.'
                  : 'Ensure all conditions are supported by planks.'}
              </Text>
            </Space>
          )}
        </div>
      </Card>
    </div>
  );
};
